import { EntityId } from '@reduxjs/toolkit';
import { ROUTER_API } from 'app/constants/api.constant';
import { CouponInDTO } from 'app/main/design-system/models/coupon.type';
import BaseApi from 'app/services/api/base.api';
import {
  CampaignCouponClaimPage,
  CampaignFiltersDTO,
  CommunicationCampaignCreationOutDTO,
  CommunicationCampaignDetailInDTO,
  CommunicationCampaignInDTO,
  CommunicationCampaignOutDTO,
  CommunicationCampaignRevenueInDTO,
  CurrentCampaign,
  NewsDTO,
  NotificationType,
  SMSCommunicationCampaignDetails,
} from '../campaign.type';
import { CampaignReminder } from '../reminder/campaignReminder';

export const notifApi = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token', timeout: 60000 });

export const createCommunicationCampaignApi = async (franchiseId: string, request: CurrentCampaign) => {
  const response = await notifApi.api.post<CommunicationCampaignCreationOutDTO>(
    `services/v2/franchises/${franchiseId}/communication-campaigns`,
    request,
    {},
  );
  return response.data;
};

export const updateCommunicationCampaignApi = async (communicationCampaignId: EntityId, payload: CurrentCampaign) => {
  const response = await notifApi.api.put<CommunicationCampaignOutDTO>(
    `services/v2/communication-campaigns/${communicationCampaignId}`,
    payload,
  );
  return response.data;
};

export const cancelCommunicationCampaignApi = async (communicationCampaignId: EntityId) => {
  const response = await notifApi.api.patch<CommunicationCampaignInDTO>(
    `services/v2/communication-campaigns/${communicationCampaignId}/cancel`,
  );
  return response.data;
};

export const getNotificationCampaignsRevenueApi = async (userId: string) => {
  const response = await notifApi.api.get<CommunicationCampaignRevenueInDTO[]>(
    `api/v2/users/${userId}/communication-campaigns/revenue`,
  );
  return response.data || [];
};

export const getCampaignReminderApi = async (): Promise<CampaignReminder> => {
  const response = await notifApi.api.get<CampaignReminder>('api/v2/campaign-reminder');
  return response.data;
};

export const getNotificationCampaignSMSCostApi = async (
  franchiseId: string,
  notificationFilters: CampaignFiltersDTO,
) => {
  const response = await notifApi.api.post<SMSCommunicationCampaignDetails>(
    `services/v2/franchises/${franchiseId}/communication-campaigns/cost`,
    notificationFilters,
  );
  return response.data || [];
};

export const getCommunicationCampaignNotifiedUsersApi = async (
  communicationCampaignFilter: CampaignFiltersDTO,
  franchiseId: string,
  notificationType?: NotificationType,
) => {
  const response = await notifApi.api.post<number>(
    `api/v2/franchises/${franchiseId}/communication-campaigns/notified-users`,
    communicationCampaignFilter,
    { params: notificationType ? { notificationType } : undefined },
  );
  return response.data;
};

export const getCommunicationCampaignInfoApi = async (campaignId: EntityId) => {
  const response = await notifApi.api.get<CommunicationCampaignDetailInDTO>(
    `api/v2/communication-campaigns/${campaignId}`,
  );
  return response;
};

export const getCommunicationCampaignStatsApi = async (campaignId: EntityId) => {
  const response = await notifApi.api.get<CommunicationCampaignRevenueInDTO>(
    `api/v2/communication-campaigns/${campaignId}/stats`,
  );
  return response;
};

export const getCommunicationCampaignCouponApi = async (campaignId: EntityId) => {
  const response = await notifApi.api.get<CouponInDTO>(`api/v2/communication-campaigns/${campaignId}/current-coupon`);
  return response;
};

export const getNewsByIdApi = async (newsId: EntityId) => {
  const response = await notifApi.api.get<NewsDTO>(`api/v2/news/${newsId}`);
  return response.data;
};

export const getCampaignCouponClaimsApi = async (campaignId: EntityId, page: number, size: number) => {
  const response = await notifApi.api.get<CampaignCouponClaimPage>(
    `api/v2/communication-campaigns/${campaignId}/coupon-claims`,
    { params: { page, size } },
  );
  return response.data;
};
